import {Injector} from "@angular/core";
import {SideSheetRef} from "@app/presentation/services/side-sheet/side-sheet-ref";
import {ActivatedRoute, Router} from "@angular/router";
import {MatBottomSheetRef} from "@angular/material/bottom-sheet";
import {RETURN_URL_QUERY_PARAM_KEY} from "@app/presentation/utils/return-url-query-param-key";
import {ModalService} from '@app/presentation/services/modal/modal.service';
import {DialogRef} from "@angular/cdk/dialog";

/**
 * This class handles exiting from any type of modal or navigating back as a fallback.
 */
export class ExitService {

    /**
     * Handles exiting from any type of modal or navigating back as a fallback.
     * @param injector -- An Angular service locator to access modal or navigation services.
     * @param value -- Data to pass when closing the modal.
     */
    static exit(injector: Injector, value?: any) {
        const modalClosed = this.closeAnyOpenModal(injector, value);

        if (this.hasReturnUrl(injector)) {
            this.navigateToReturnUrl(injector);
            return;
        }

        // If no modal was closed and there's no return URL, navigate back in the browser history
        if (!modalClosed) {
            window.history.back();
        }
    }

    /**
     * Attempts to close any open modal (SideSheet or BottomSheet).
     * @param injector -- Angular service locator.
     * @param value -- Optional data to pass when closing the modal.
     * @returns True if a modal was closed, false otherwise.
     */
    private static closeAnyOpenModal(injector: Injector, value?: any): boolean {
        try {
            const sideSheet = injector.get(SideSheetRef);
            sideSheet.close(value);
            return true;
        } catch {}

        try {
            const bottomSheet = injector.get(MatBottomSheetRef);
            bottomSheet.dismiss(value);
            return true;
        } catch {}

        try {
            const dialog = injector.get(DialogRef);
            dialog.close(value);
            return true;
        } catch {}

        return false;
    }

    /**
     * Navigates to the return URL if it exists, otherwise navigates to the previous page.
     * @param injector
     * @param value
     */
    static back(injector: Injector, value?: any) {

        // If it's a Side Sheet, it uses its own back to show the previous screen content
         try {
            const sideSheet = injector.get(SideSheetRef);
            sideSheet.back(value);
            return;
        } catch {}

        ExitService.exit(injector, value);
    }

    static hasReturnUrl(injector: Injector): boolean {
        const returnUrl = this.getReturnUrl(injector);
        return returnUrl != null;
    }

    static navigateToReturnUrl(injector: Injector) {
        const router = injector.get(Router);
        const returnUrl = this.getReturnUrl(injector);
        if (returnUrl == null) {
            return;
        }
        router.navigateByUrl(returnUrl);
    }

    /**
     * Retrieves the decoded return URL for the modal,
     * checking both modal-specific data and route query parameters.
     * @param injector - Angular dependency injector to access modal data and route parameters
     * @returns The decoded return URL if found, otherwise null
     */
    static getReturnUrl(injector: Injector): string | null {

        const returnUrl = this.getReturnUrlFromModalData(injector);
        if (returnUrl != null) {
            return returnUrl;
        }

        try {
            const activatedRoute = injector.get(ActivatedRoute);
            const url = activatedRoute.snapshot.queryParamMap.get(RETURN_URL_QUERY_PARAM_KEY);
            if (url != null) {
                return decodeURIComponent(url);
            }
        } catch {}

        return  null
    }

    /**
     * Returns the decoded Return URL from the modal data.
     * @param injector 
     * @returns 
     */
    private static getReturnUrlFromModalData(injector: Injector): string | null {
        const modalData = ModalService.getModalData(injector);
        const encodedReturnUrl = modalData?.queryParams?.[RETURN_URL_QUERY_PARAM_KEY];
        if (encodedReturnUrl) {
            return ExitService.decodeReturnUrl(encodedReturnUrl);
        }
        return null;
    }

    /**
     * Decodes a potentially multi-encoded URI return URL. Handles cases where the URL
     * was encoded multiple times by recursively decoding until no further encoding remains.
     *
     * @param encodedReturnUrl - The URI-encoded return URL (may be double or multiple-encoded)
     * @returns The fully decoded URL string
     */
    private static decodeReturnUrl(encodedReturnUrl: string): string     {
        let finalDecoded = decodeURIComponent(encodedReturnUrl);
        while (finalDecoded !== decodeURIComponent(finalDecoded)) {
            finalDecoded = decodeURIComponent(finalDecoded);
        }
        return finalDecoded;
    }


}
